import { Component } from 'react'
import PropTypes from 'prop-types'
import screenfull from 'screenfull'
import classnames from 'classnames'
import { SMALL, MEDIUM, LARGE, FULL } from '@constants/timeline_view_sizes.js'

export default class TimelineSizeSelector extends Component {
  constructor(props) {
    super(props)
    this.state = {
      fullscreenEnabled: screenfull.isEnabled,
    }
    // setup stuff to deal with exiting fullscreen mode
    const { namespace, viewSize } = props
    this.prevSize = viewSize
    this.boundScreenfullChangeHandler = this.handleScreenfullChange.bind(this, namespace)
    if (screenfull.isEnabled) {
      // Add an always-on listener for fullscreen changes, it's a bit tricky to properly
      // remove listeners with args so using a simpler method works better here
      screenfull.on('change', this.boundScreenfullChangeHandler)
    }
  }

  componentWillUnmount() {
    if (screenfull.isEnabled) {
      screenfull.off('change', this.boundScreenfullChangeHandler)
    }
  }

  handleSmallClick = () => {
    this.handleSizeChange(SMALL)
  }

  handleMediumClick = () => {
    this.handleSizeChange(MEDIUM)
  }

  handleLargeClick = () => {
    this.handleSizeChange(LARGE)
  }

  handleSizeChange(viewSize) {
    const { namespace, scope, onChange } = this.props
    onChange(namespace, scope, viewSize)
  }

  // Note: if going into fullscreen, it must happen as a result of a direct user interaction
  handleFullscreenClick = event => {
    const { namespace, scope, onChange, viewSize } = this.props
    if (screenfull.isEnabled) {
      const el = document.getElementById('timeline-fullscreenable')
      // store the current size so we can go back to that when exiting fullscreen
      this.prevSize = viewSize
      screenfull.request(el)
      onChange(namespace, scope, FULL)
    } else {
      onChange(namespace, scope, LARGE)
    }
  }

  handleScreenfullChange = e => {
    if (!screenfull.isFullscreen) {
      // no longer full screen, lets try to go back to the previous size we were at
      const { namespace, scope, onChange } = this.props
      const prevSize = this.prevSize || SMALL
      onChange(namespace, scope, prevSize)
    }
  }

  getLargestSizeButtonContent() {
    const { viewSize, allowFullscreenControl } = this.props
    const { fullscreenEnabled } = this.state
    if (allowFullscreenControl) {
      const buttonIcon = (
        <div className="size-button-icon size-button-icon--fullscreen">
          <span className="corner corner--tl"></span>
          <span className="corner corner--tr"></span>
          <span className="corner corner--br"></span>
          <span className="corner corner--bl"></span>
        </div>
      )
      let label, clickHandler, selected
      if (fullscreenEnabled) {
        label = 'Fullscreen mode'
        clickHandler = this.handleFullscreenClick
        selected = viewSize === FULL
      } else {
        label = 'Large view'
        clickHandler = this.handleLargeClick
        selected = viewSize === LARGE
      }
      return (
        <div
          role="button"
          onClick={clickHandler}
          className={classnames(
            'size-button',
            'size-button--large',
            { 'size-button--selected': selected },
            'hint hint--top-left hint--timetoast'
          )}
          aria-label={label}
        >
          {buttonIcon}
        </div>
      )
    }
    return null
  }

  render() {
    const { viewSize } = this.props
    const largestSizeButton = this.getLargestSizeButtonContent()
    return (
      <div className="timeline-size-buttons">
        <div
          role="button"
          onClick={this.handleSmallClick}
          className={classnames(
            'size-button',
            'size-button--small',
            { 'size-button--selected': viewSize === SMALL },
            'hint hint--top-left hint--timetoast'
          )}
          aria-label="Default view"
        >
          <div className="size-button-icon"></div>
        </div>
        <div
          role="button"
          onClick={this.handleMediumClick}
          className={classnames(
            'size-button',
            'size-button--medium',
            { 'size-button--selected': viewSize === MEDIUM },
            'hint hint--top-left hint--timetoast'
          )}
          aria-label="Wide view"
        >
          <div className="size-button-icon"></div>
        </div>
        {largestSizeButton}
      </div>
    )
  }
}
TimelineSizeSelector.propTypes = {
  namespace: PropTypes.string.isRequired,
  scope: PropTypes.string.isRequired,
  viewSize: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
}
